<script>
  import { closeCartDialog } from "#lib/stores.js";
  import { cartStore } from "#lib/stores/cart.js";
  // Units, not lines: a line Shopify zeroed out for an uncovered country is
  // not an item the shopper has. See $lib/utils/cart-quantity.js.
  import { cartUnitCount } from "#lib/utils/cart-quantity.js";
  import Burger from "../svg/Burger.svelte";
  import { onMount, onDestroy } from "svelte";
  import { browser } from "$app/environment";
  import { page } from "$app/state";
  import { toStore } from "svelte/store";
  // SvelteKit 3 removes $app/stores. This component is still legacy mode, where
  // $app/state alone is not reactive, so what its `$:` statements read is bridged
  // back into a store with Svelte's toStore (hub ledger 2026-10-23-125).
  const pageUrl = toStore(() => page.url);
  import { userCountryStore, showCountryPopupStore, detectedCountryStore } from "#lib/stores/countryStore.js";
  import { captureEvent } from "#lib/utils/posthog.js";
  import { openSignup10 } from "#lib/stores/signup10.js";
  import { rewardSymbol } from "#lib/signup10/reward.js";
  import { campaign } from "#lib/stores/campaign.js";
  import { withSymbol } from "#lib/campaign.js";

  // Open the €10-off survey modal (rendered once in +layout.svelte). The whole
  // promo bar triggers it, as on SOLDR: the button is the keyboard stop, the
  // bar around it takes the rest of the clicks (only the 262px of text did,
  // hub ledger 2026-10-01-091).
  function openSignup() {
    openSignup10("topbar");
  }

  // Dismiss (x) hides the bar for this page view only; it returns on reload,
  // so the offer keeps showing. With the bar gone --banner-height drops to 0
  // at the root (html.promo-dismissed, _vars.scss), and every offset built on
  // it - the header's padding, the product page's top padding - follows in
  // CSS, without waiting for a measurement.
  let promoVisible = true;
  function dismissPromo(e) {
    e.stopPropagation();
    promoVisible = false;
  }
  $: if (browser) document.documentElement.classList.toggle("promo-dismissed", !promoVisible);


  let isBurgerOpen = false;

  // The rendered height of the promo bar, published for anything positioned
  // against the viewport. The burger panel is the one that needs it: it sits
  // at the bottom of the header, which is only y=0 + --navbar-height while
  // nothing is above the header. `.hero-banner` is, on the home and product
  // pages, and it renders 35.19px rather than the 2.2rem --banner-height
  // implies - so the offset is measured, not assumed (hub rule R002, ledger
  // 2026-09-22-065). offsetHeight, not clientHeight: padding and border count,
  // and a 2px shortfall parks the panel inside the header.
  let bannerHeight = 0;

  function publishBarHeight(px) {
    if (!browser) return;
    document.documentElement.style.setProperty("--promo-bar-height", `${px}px`);
  }

  let windowWidth = 0; // Default to mobile size
  let mounted = false;
  let isOverHero = $pageUrl.pathname === "/";
  let isOverDarkSection = false;

  onMount(() => {
    windowWidth = window.innerWidth; // Set width after mounting
    mounted = true;

    const handleHeroVisibility = (event) => {
      isOverHero = event.detail.isVisible;
    };

    const handleDarkSectionVisibility = (event) => {
      isOverDarkSection = event.detail.isVisible;
    };

    window.addEventListener("heroVisibilityChanged", handleHeroVisibility);
    window.addEventListener(
      "darkSectionVisibilityChanged",
      handleDarkSectionVisibility
    );

    return () => {
      window.removeEventListener("heroVisibilityChanged", handleHeroVisibility);
      window.removeEventListener(
        "darkSectionVisibilityChanged",
        handleDarkSectionVisibility
      );
    };
  });

  $: isHomePage = $pageUrl.pathname === "/";
  $: isProductPage = $pageUrl.pathname.includes("/products/");
  // Zero while the bar is not on screen: the open menu hides it
  // (`.hero-banner.burger-open { display: none }`) and the header's padding
  // goes with it, so the header is back at y=0 and the panel must not be
  // pushed down by a bar nobody can see. Zero on the pages without a bar too,
  // so they are unchanged.
  $: publishBarHeight(promoVisible && !isBurgerOpen ? bannerHeight : 0);

  // A header that unmounts must not leave a stale offset behind.
  onDestroy(() => {
    publishBarHeight(0);
    if (browser) document.documentElement.classList.remove("promo-dismissed");
  });

  // Watch for page navigation and reset hero state
  $: if (mounted && $pageUrl.pathname) {
    if (isHomePage) {
      // On homepage, check hero visibility immediately
      requestAnimationFrame(() => {
        const heroSection = document.querySelector('.hero-container');
        if (heroSection) {
          const rect = heroSection.getBoundingClientRect();
          isOverHero = rect.bottom > 0;
        } else {
          // Hero not mounted yet, assume at top
          isOverHero = window.scrollY === 0;
        }
      });
    } else {
      // Not on homepage, ensure hero state is false
      isOverHero = false;
    }
  }

  // Country name mapping (alphabetically sorted by country code)
  const countryNames = {
    'AD': 'Andorra',
    'AL': 'Albania',
    'AM': 'Armenia',
    'AT': 'Austria',
    // 'AU': 'Australia',
    'BA': 'Bosnia and Herzegovina',
    'BE': 'Belgium',
    'BG': 'Bulgaria',
    'BY': 'Belarus',
    'CH': 'Switzerland',
    'CY': 'Cyprus',
    'CZ': 'Czech Republic',
    'DE': 'Germany',
    'DK': 'Denmark',
    'EE': 'Estonia',
    'GR': 'Greece',
    'ES': 'Spain',
    'FI': 'Finland',
    'FR': 'France',
    'GB': 'United Kingdom',
    'GE': 'Georgia',
    'HR': 'Croatia',
    'HU': 'Hungary',
    'IE': 'Ireland',
    'IS': 'Iceland',
    'IT': 'Italy',
    'KZ': 'Kazakhstan',
    'LI': 'Liechtenstein',
    'LT': 'Lithuania',
    'LU': 'Luxembourg',
    'LV': 'Latvia',
    'MC': 'Monaco',
    'MD': 'Moldova',
    'ME': 'Montenegro',
    'MK': 'North Macedonia',
    'MT': 'Malta',
    'NL': 'Netherlands',
    'NO': 'Norway',
    // 'NZ': 'New Zealand',
    'PL': 'Poland',
    'PT': 'Portugal',
    'RO': 'Romania',
    'RS': 'Serbia',
    'RU': 'Russia',
    'SE': 'Sweden',
    'SI': 'Slovenia',
    'SK': 'Slovakia',
    'SM': 'San Marino',
    'TR': 'Turkey',
    'UA': 'Ukraine',
    'US': 'United States',
    'VA': 'Vatican City',
    'XK': 'Kosovo'
  };

  // Function to open country selection popup
  function openCountrySelector() {
    showCountryPopupStore.set(true);
  }

  // Get country display name
  $: countryDisplayName = $userCountryStore ? countryNames[$userCountryStore] || $userCountryStore : 'Select Country';

  $: bannerCurrencySymbol = rewardSymbol($userCountryStore);
  $: hw = $campaign?.id === "halloween";
  $: hwTagline = hw ? withSymbol($campaign.bannerTagline, $userCountryStore) : "";
  
  // For debugging - log the current state
  $: if (typeof window !== 'undefined') {
    // console.log('Country selector state:', { 
    //   userCountry: $userCountryStore, 
    //   displayName: countryDisplayName 
    // });
  }
</script>

<svelte:head>
  {#if hw}
    <!-- The Halloween bar's height, in the HTML itself so the first paint is
         right: the header padding and the product page's top padding are
         built on --banner-height and would otherwise sit 30px under a 64px
         bar until hydration. :not(.promo-dismissed) keeps Dismiss at 0.
         A constant string, nothing from outside reaches it. -->
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    {@html `<style data-campaign="halloween">:root:not(.promo-dismissed){--banner-height:52px}@media (min-width:641px){:root:not(.promo-dismissed){--banner-height:64px}}</style>`}
  {/if}
</svelte:head>


<svelte:window bind:innerWidth={windowWidth} />

<div
  class="parent-container"
  class:homepage={isHomePage}
  class:productpage={isProductPage}
  class:scrolled={isOverHero}
  class:dark-section-active={isOverDarkSection}
  class:burger-open={isBurgerOpen}
>
<!-- One block, not two: the isHomePage and isProductPage branches were
     byte-identical, so the duplicate could only ever drift.
     A <button>, not an <a href="#signup">: it opens the survey modal and
     preventDefault'd its own navigation, so it was never a link. As a button
     it gets Enter AND Space, the right role and a focus ring natively, with no
     hand-rolled keydown handler to keep in sync. -->
{#if promoVisible}
<!-- Every page with a header, as on SOLDR (hub ledger 2026-10-01-092): it
     was the home and product pages only. The bar is not a control itself -
     the CTA and Dismiss are its two keyboard stops - so it takes the click
     without nesting one button in another. -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  class="hero-banner ph-no-deadclick"
  class:burger-open={isBurgerOpen}
  class:halloween={hw}
  data-campaign={hw ? "halloween" : null}
  data-testid={hw ? "campaign-bar" : null}
  bind:offsetHeight={bannerHeight}
  on:click={openSignup}
>
  {#if hw}
    <!-- Halloween (lib/campaign.js), the same bar as SOLDR's. Taller than
         the everyday one: --banner-height follows (the <svelte:head> style
         above), so the header and product-page padding move with it, and the
         burger panel uses the measured --promo-bar-height as before. The
         graphics are decoration; the button carries the offer as its name. -->
    <img class="hw-special" src="/images/campaigns/halloween/halloween-special-bar.webp" alt="" width="729" height="144" />
  {/if}
  <button
  type="button"
  class="banner-cta ph-no-deadclick"
  class:hw-cta={hw}
  data-testid="signup10-open"
  aria-label={hw ? `Halloween special: ${$campaign.percent}% off. Get ${hwTagline.replace(/^for /, "")}` : null}
  on:click|stopPropagation={openSignup}
>
{#if hw}
<img class="hw-off" src="/images/campaigns/halloween/15-off-bar.webp" alt="" width="442" height="162" />
<span class="hw-tagline"><span class="hw-verb-click">Click</span><span class="hw-verb-tap">Tap</span> {hwTagline}</span><span class="bar-arrow hw-arrow" aria-hidden="true">→</span>
{:else}
<span class="bar-label">Get {bannerCurrencySymbol}10 off</span><span class="bar-arrow" aria-hidden="true">→</span>
{/if}
</button>
  <button
    type="button"
    class="banner-close"
    aria-label="Dismiss"
    data-testid="signup10-dismiss"
    on:click={dismissPromo}
  >&times;</button>
</div>
{/if}
  <header class:no-banner-height={!isHomePage || !isProductPage} class:is-open={isBurgerOpen}>
    <nav class="flex-between">
      <a class="logo-container" href="/">
        {#if (isHomePage && isOverHero)  || isBurgerOpen || isOverDarkSection}
          <img
            class="logo mobile-logo"
            src="/images/logo/logo-wht-small_1.webp"
            alt="White Logo Small"
            loading="eager"
            fetchpriority="high"
          />
          <img
            class="logo desktop-logo"
            src="/images/logo/Logo_desktop_wht_1.webp"
            alt="White Logo Large"
            loading="eager"
            fetchpriority="high"
          />
        {:else}
          <img
            class="logo mobile-logo"
            src="/images/logo/logo-black-small_1.webp"
            alt="Black Logo Small"
            loading="eager"
            fetchpriority="high"
          />
          <img
            class="logo desktop-logo"
            src="/images/logo/Logo_desktop_1.webp"
            alt="Black Logo Large"
            loading="eager"
            fetchpriority="high"
          />
        {/if}
      </a>
      <div class="flex-nav-row">
        <div class="desktop-nav">
          <a href="/products/the-glorb/" on:click={() => captureEvent('cta_click', { location: 'header_nav' })}>SHOP</a>
          <a href="/faq/">FAQ</a>
          <a href="/about/">MISSION</a>
        </div>
        <button
          on:click={openCountrySelector}
          class="country-selector-btn"
          title="Change shipping country"
        >
          <span class="shipping-label">Ship to:</span>
          <span class="country-name">{countryDisplayName}</span>
          
        </button>
        <button
          on:click={() => {
            closeCartDialog.set(false);
          }}
          class="cl-btn ph-no-deadclick">CART ({cartUnitCount($cartStore.items)})</button
        >
        <Burger bind:isOpen={isBurgerOpen} />
      </div>
    </nav>
  </header>
</div>


<style lang="scss">
  // Base container styles (for non-homepage, non-productpage) add :not(.productpage) wyhen u want to go live with halloween
  /* div.parent-container:not(.homepage):not(.productpage) {
    position: sticky;
    width: 100%;
    z-index: 1;
    top: 0;
  } */

  // Homepage base styles (white header)
  div.parent-container.homepage {
    position: fixed;
    width: 100%;
    top: 0;
    z-index: 1;
    padding-top: var(--banner-height);

    header {
      background-color: white;
      color: black;

      .desktop-nav a {
        color: black;

        &::after {
          background-color: black;
        }
      }

      button {
        color: black;
      }
    }
  }

  // Every other page: the container stays in the flow, and the bar (absolute,
  // top: 0) sits in its padding exactly as on the home and product pages.
  div.parent-container:not(.homepage):not(.productpage) {
    position: relative;
    padding-top: var(--banner-height);
  }

  div.parent-container.homepage.burger-open,
  div.parent-container.productpage.burger-open,
  div.parent-container.burger-open {
    padding-top: 0 !important;
  }

 

  // Product page base styles (fixed position, white header) enable when u want to go live with halloween
  div.parent-container.productpage {
    position: fixed;
    width: 100%;
    top: 0;
    z-index: 1;
    // Same construction as the homepage above, and for the same reason: the
    // bar is absolutely positioned at the container's top: 0, so the header
    // has to start below it. This used to be `padding-top` on the header
    // itself, applied only once an IntersectionObserver reported the bar
    // visible - which meant that for the first 0.2s (fast connection) to 2s
    // (throttled mobile) of every product-page load the header sat at y=0 and
    // the orange bar was painted straight over the wordmark. Measured on
    // production and on a local preview, hub ledger 2026-09-22-066.
    padding-top: var(--banner-height);

    header {
      background-color: white;
      color: black;

      .desktop-nav a {
        color: black;

        &::after {
          background-color: black;
        }
      }

      button {
        color: black;
      }
    }
  }

  // Homepage transparent header (when scrolled)
  div.parent-container.homepage.scrolled {
    header {
      background-color: transparent;
      padding-top: 0;
      color: white;

      .desktop-nav a {
        color: white;

        &::after {
          background-color: white;
        }
      }

      button {
        color: white;
      }
    }
  }

  // Homepage banner padding when scrolled and banner visible
  /* div.parent-container.homepage.scrolled.banner-visible {
    header {
      padding-top: var(--banner-height);
    }
  } */

  // Regular header styles (unchanged for other pages)
  header {
    background-color: white;
    color: black;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: var(--navbar-height);

    .desktop-nav a {
      color: black;

      &::after {
        background-color: black;
      }
    }

    button {
      color: black;
    }
  }

  // About page override
  :global([data-page="about"]) header {
    background-color: #46cdf6;
  }

  // Burger menu override for all states

  /* header.is-open.no-banner-height nav.menu.no-banner-height {
    padding-top: 0 !important;
  } */

  header.is-open {
    background-color: var(--secondary-color) !important;
    color: white !important;


    button {
      color: white !important;
    }

    .desktop-nav a {
      color: white !important;

      &::after {
        background-color: white !important;
      }
    }
  }

  .hero-banner.burger-open {
    display: none;
  }

  nav {
    width: 100%;
  }
  .logo-container {
    height: auto;
    width: auto;
    padding-left: var(--padding);
    display: flex;
    align-items: center;
  }

  .logo {
    width: auto;
    height: 22.5px;
  }

  .desktop-logo {
    display: none;
  }

  .flex-nav-row {
    gap: 0rem;
    display: flex;
    flex-direction: row;
  }

  .desktop-nav {
    display: none;
    gap: 2rem;
    align-items: center;

    a {
      color: black;
      text-decoration: none;
      font-weight: 500;
      position: relative;

      &::after {
        content: "";
        position: absolute;
        width: 100%;
        height: 1px;
        bottom: -4px;
        left: 0;
        background-color: currentColor;
        transform: scaleX(0);
        transform-origin: right;
        transition: transform 0.3s ease;
      }

      &:hover::after {
        transform: scaleX(1);
        transform-origin: left;
      }
    }
  }

  button.cl-btn {
    position: relative;
  }

  // Country selector button styles
  .country-selector-btn {
    display: none; // Hide on mobile by default
    align-items: center;
    gap: 0.5rem;
    margin-left: 4rem;
    /* padding: 0.5rem 1rem; */
    background: transparent;
    border: none;

    color: black;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      border-color: rgba(0, 0, 0, 0.3);
    }

    .shipping-label {
      font-size: 0.75rem;
      opacity: 0.7;
      margin-right: 0.25rem;
    }

  
    

    
  }

  .hero-banner {
    height: var(--banner-height);
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    background-color: #FF6F00;
    cursor: pointer;

    /* `.banner-cta`, not `a`: this is a <button> now. Scoped by class so the
       generic `button` rules further down this stylesheet cannot claim it. */
    .banner-cta {
      appearance: none;
      background: none;
      border: 0;
      margin: 0;
      padding: 0;
      /* Explicit: this stylesheet has a bare `button { width: 100% }` in its
         >=860px block (for the nav buttons), and as an <a> this element was
         never subject to it. Left implicit, the banner CTA stretched to the
         full viewport width. */
      width: auto;
      box-sizing: content-box;

      font-size: 14px;
      font-family: "Montreal regular", system-ui, -apple-system, sans-serif;
      color: #fff;
      font-weight: 600;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-decoration: none;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
    }

    // Underline hidden at rest, wipes in on hover (matches the nav-link style).
    .bar-label {
      position: relative;
    }
    .bar-label::after {
      content: "";
      position: absolute;
      left: 0;
      bottom: -2px;
      width: 100%;
      height: 1px;
      background: #fff;
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.25s ease;
    }
    .banner-cta:hover .bar-label::after,
    .banner-cta:focus-visible .bar-label::after {
      transform: scaleX(1);
    }

    // Arrow nudges right on hover.
    .bar-arrow {
      display: inline-block;
      transition: transform 0.25s ease;
    }
    .banner-cta:hover .bar-arrow,
    .banner-cta:focus-visible .bar-arrow {
      transform: translateX(4px);
    }

    @media (prefers-reduced-motion: reduce) {
      .bar-label::after,
      .bar-arrow {
        transition: none;
      }
    }

    .hero-banner-span {
      text-decoration: underline;
      font-weight: 700;
    }

    /* Scoped by class, and width set: the bare `button { width: 100% }` in
       the >=860px block below would otherwise stretch it across the bar. */
    .banner-close {
      position: absolute;
      top: 50%;
      right: 8px;
      transform: translateY(-50%);
      width: 32px;
      height: 32px;
      padding: 0;
      margin: 0;
      border: 0;
      background: none;
      color: #fff;
      font-size: 20px;
      line-height: 1;
      cursor: pointer;
      opacity: 0.85;
    }
    .banner-close:hover,
    .banner-close:focus-visible {
      opacity: 1;
    }

   
  }

  @media (min-width: 860px) {
    .mobile-logo {
      display: none;
    }

    .desktop-logo {
      display: block;
    }

    .desktop-nav {
      display: flex;
    }

    .country-selector-btn {
      display: flex;
    }

    .hero-banner .banner-cta {
      font-size: 16px;
    }

    button {
      font-size: 1rem;
      width: 100%;
     
    }
  }

  // Dark section override
  div.parent-container.dark-section-active header {
    background-color: black !important;
    color: white !important;

    .desktop-nav a {
      color: white !important;

      &::after {
        background-color: white !important;
      }
    }

    button {
      color: white !important;
    }

    .country-selector-btn {
      color: white !important;
     

      &:hover {
        background: rgba(255, 255, 255, 0.1) !important;
        border-color: rgba(255, 255, 255, 0.5) !important;
      }

     
    }
  }

  /* ---- Halloween bar (lib/campaign.js) --------------------------------
     Same layout and graphics as SOLDR's. Each graphic is cropped so its white
     lettering sits in the exact middle of the image (the drop shadow falls
     downward, so a crop to everything visible left the letters riding high).
     Height comes from --banner-height, set in <svelte:head> above. */
  div.hero-banner.halloween {
    background: linear-gradient(90deg, #b70600 0%, #f25d11 100%);
    justify-content: space-between;
    gap: 12px;
    padding: 0 56px 0 24px;
    box-sizing: border-box;

    .hw-special { height: 48px; width: auto; display: block; flex: 0 0 auto; }
    /* Static, so the absolutely placed 15% OFF centres on the bar. */
    .hw-cta { position: static; gap: 8px; }
    .hw-off {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      height: 54px;
      width: auto;
      display: block;
      pointer-events: none;
      transition: transform 0.25s ease;
    }
    .hw-cta:hover .hw-off { transform: translate(-50%, -50%) scale(1.04); }
    .hw-tagline,
    .hw-arrow {
      font-family: "Montreal bold", system-ui, -apple-system, sans-serif;
      font-size: 18px;
      letter-spacing: 0.01em;
      text-transform: uppercase;
      color: #fff;
    }
  }

  /* Phones, as in the design: Halloween Special left, 15% OFF right, nothing
     else. The tagline does not fit at 320px and 15% OFF already says the
     offer; no dismiss either, the design has none. */
  /* "Click" with a mouse, "Tap" on a touch screen. */
  .hw-verb-tap { display: none; }
  @media (hover: none) {
    .hw-verb-click { display: none; }
    .hw-verb-tap { display: inline; }
  }

  /* Phones: the tap line left, 15% OFF right, no dismiss. The line takes
     Halloween Special's place: phones showed only the two graphics and
     tapped the bar at half the desktop rate (Simon, 2026-10-14). */
  @media (max-width: 640px) {
    div.hero-banner.halloween {
      padding: 0 16px;
      .hw-special,
      .banner-close { display: none; }
      .hw-cta { width: 100%; justify-content: space-between; gap: 0; }
      .hw-tagline { order: 0; font-size: 16px; white-space: nowrap; }
      .hw-arrow { order: 1; margin: 0 auto 0 6px; font-size: 16px; }
      .hw-off { order: 2; position: static; transform: none; height: 42px; }
      .hw-cta:hover .hw-off { transform: scale(1.04); }
    }
  }
  @media (max-width: 359px) {
    div.hero-banner.halloween {
      .hw-off { height: 38px; }
      .hw-tagline,
      .hw-arrow { font-size: 14px; }
    }
  }
  @media (min-width: 641px) and (max-width: 900px) {
    div.hero-banner.halloween {
      .hw-tagline, .hw-arrow { font-size: 14px; }
      .hw-special { height: 35px; }
      .hw-off { height: 44px; }
    }
  }
</style>
